import cn from 'classnames';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FieldPath, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { TextField } from '@react-md/form';
import type { FetchBaseQueryError } from '@services/df/baseApi';
import { ResetPasswordRequest, usePasswordRecoveryResetCreateMutation } from '@services/df/auth';
import { FeatureContent } from '@components/feature-content';
import { FormField, NonFieldErrors } from '@components/form-field';
import { SubmitButton } from '@components/submit-button';
import { useRedirectUrl, useValidation } from '@hooks';
import { reverse, setApiErrors } from '@utils';
import { useGetResizeRef } from '@utils/parant-site-comunicator';
import { createChangePasswordValidation } from '../form-validation';
import { AuthSectionHead } from '../components';
import { passwordRecoveryId } from '../helpers';
import { PasswordRecoveryFormId } from '../constants';

import styles from '../auth-page.module.scss';

export interface ResetPasswordForm extends Omit<ResetPasswordRequest, 'reset_token'> {
    confirm_password: string;
}

export const ChangePasswordPage: React.FC = () => {
    const navigate = useNavigate();
    const { token } = useParams<{
        company: string;
        token: string;
    }>();
    const redirectUrl = useRedirectUrl();
    const next = redirectUrl || reverse('signIn');

    const [reset, { isLoading: isChangePasswordSending, isSuccess }] = usePasswordRecoveryResetCreateMutation();
    const validationSchema = useValidation(createChangePasswordValidation);
    const {
        control,
        handleSubmit,
        setError,
        formState: { errors },
    } = useForm<ResetPasswordForm>({
        defaultValues: {
            new_password: '',
            confirm_password: '',
        },
        resolver: yupResolver(validationSchema),
    });
    const containerRef = useGetResizeRef();

    const submit: SubmitHandler<ResetPasswordForm> = async (form, event) => {
        if (!token) return;

        const { new_password } = form;

        try {
            await reset({
                resetPasswordRequest: { reset_token: token!, new_password },
            }).unwrap();

            navigate(next, { replace: true });
        } catch (error) {
            const errors = (error as FetchBaseQueryError).data;
            setApiErrors(setError, errors);
        }
    };

    const { t } = useTranslation();

    return (
        <div id={passwordRecoveryId('page')} className={styles.layout} ref={containerRef}>
            <div className="container">
                <div className={cn('rounded-box', styles.box)}>
                    <form id={PasswordRecoveryFormId} onSubmit={handleSubmit(submit)}>
                        <div className={cn('fieldset-grid', 'base-paddings')}>
                            <AuthSectionHead title={t('titles.passwordRecovery', 'Password recovery')} />

                            <FormField
                                prefix={PasswordRecoveryFormId}
                                name="new_password"
                                control={control}
                                component={TextField}
                                label={t('labels.newPassword', 'New password')}
                                widgetProps={{
                                    type: 'password',
                                    isRightAddon: false,
                                }}
                            />
                            <FormField
                                prefix={PasswordRecoveryFormId}
                                name="confirm_password"
                                control={control}
                                component={TextField}
                                label={t('labels.confirmNewPassword', 'Confirm new password')}
                                widgetProps={{
                                    type: 'password',
                                    isRightAddon: false,
                                }}
                            />
                            <NonFieldErrors errors={errors} />

                            <div className={cn('fieldset-grid-cell', 'flex')}>
                                <FeatureContent
                                    contentKey={passwordRecoveryId('submit')}
                                    fallback={t('buttons.confirm', 'Confirm')}
                                    wrapper={props => (
                                        <SubmitButton id={props.id!} isSending={isChangePasswordSending}>
                                            {props.children}
                                        </SubmitButton>
                                    )}
                                />
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};
